import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { UserProfileService } from '@core/services/user.service';

interface User {
  user?: {
    isOnboardingCompleted?: number;
  };
}

function onBoardingGuard(redirectOnIncomplete: boolean): CanActivateFn {
  return () => {
    const userProfileService = inject(UserProfileService);
    const router = inject(Router);

    return userProfileService.getUser().pipe(
      take(1), // Ensure the observable completes
      switchMap((cachedUser: User | null) => {
        if (cachedUser) {
          return handleGuardDecision(cachedUser, redirectOnIncomplete, router);
        }

        // Fetch user details if not cached
        return userProfileService.getUserDetails().pipe(
          take(1), // Ensure the observable completes
          switchMap((user: User) => handleGuardDecision(user, redirectOnIncomplete, router)),
          catchError((err) => {
            console.error('Error fetching user details:', err);
            // router.navigateByUrl('/'); // Redirect to a safe page
            return of(false);
          })
        );
      }),
      catchError((err) => {
        console.error('Error in user profile service:', err);
        // router.navigateByUrl('/'); // Redirect to a safe page
        return of(false);
      })
    );
  };
}

function handleGuardDecision(user: User, redirectOnIncomplete: boolean, router: Router) {
  if (user.user?.isOnboardingCompleted === 0) {
    if (redirectOnIncomplete) {
      router.navigate(['/getting-started'], { replaceUrl: true }); // Prevent navigation loop
      return of(false);
    }
    return of(true);
  } else {
    if (!redirectOnIncomplete) {
      router.navigate(['/'], { replaceUrl: true }); // Prevent navigation loop
      return of(false);
    }
    return of(true);
  }
}

export { onBoardingGuard };
